import { useDismissAiProcessingItem } from '@hooks/useAiProcessingStats';
import {
  Badge,
  NewBox as Box,
  Button,
  Flex,
  Progress,
  Text,
  Tooltip,
  useToast,
} from '@retracedgmbh/ui-components-lib';
import classNames from 'classnames';
import { useState } from 'react';
import { useQueryClient } from '@/hooks/useApi';
import useTranslator from '@/hooks/useTranslator';
import * as api from '@/services/api';
import CancelAnalysisDialog from './CancelAnalysisDialog';
import type { AiProcessingItem } from './types';

interface ItemCardProps {
  item: AiProcessingItem;
  index: number;
  onCta: (item: AiProcessingItem) => void;
}

const STATUS_STYLE = {
  COMPLETED: {
    border: 'tw:border-border-positive',
    bg: 'tw:bg-background-positive-subtle',
    icon: 'fa-regular fa-circle-check tw:text-content-positive',
    badgeType: 'secondary' as const,
  },
  FAILED: {
    border: 'tw:border-border-negative',
    bg: 'tw:bg-background-negative-subtle',
    icon: 'fa-regular fa-triangle-exclamation tw:text-content-negative',
    badgeType: 'destructive' as const,
  },
  PROCESSING: {
    border: 'tw:border-border-primary',
    bg: 'tw:bg-background-primary',
    icon: 'fa-regular fa-file tw:text-content-brand',
    badgeType: 'primary' as const,
  },
} as const;

const STATUS_LABEL_KEYS = {
  COMPLETED: 'itemCard.status.completed',
  FAILED: 'itemCard.status.failed',
  PROCESSING: 'itemCard.status.processing',
} as const;

export default function ItemCard({ item, index, onCta }: ItemCardProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const dismissMutation = useDismissAiProcessingItem();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { getTranslation } = useTranslator({ page: 'ai-processing' });

  const config = STATUS_STYLE[item.status];
  const badgeLabel = getTranslation(STATUS_LABEL_KEYS[item.status]);

  const handleDismiss = async () => {
    try {
      if (item.assetType === 'AUDIT' && isProcessing) {
        await api.backend.cancelCapaWorkflow({ workflowId: item.assetId });
      }
      if (item.assetType === 'CERTIFICATE' && isProcessing) {
        await api.backend.cancelCertificateAiWorkflow({
          workflowId: item.assetId,
        });
      }
      await dismissMutation.mutateAsync(item.id);
      queryClient.invalidateQueries({ queryKey: ['ai-processing-summary'] });
      queryClient.invalidateQueries({ queryKey: ['ai-processing-stats'] });
    } catch (error) {
      toast({ title: (error as Error).message, variant: 'destructive' });
    }
  };

  const isProcessing = item.status === 'PROCESSING';
  const isAuditCompleted =
    item.assetType === 'AUDIT' && item.status === 'COMPLETED';
  const isCertificateCompleted =
    item.assetType === 'CERTIFICATE' && item.status === 'COMPLETED';
  const isTracingProcessing = item.assetType === 'TRACING' && isProcessing;
  const isCertifiedMaterialsCompleted =
    item.assetType === 'CERTIFIED_MATERIALS' && item.status === 'COMPLETED';
  const tooltipLabel = isProcessing
    ? getTranslation('itemCard.cancelJob')
    : getTranslation('itemCard.dismiss');

  return (
    <>
      <Flex
        padding='md'
        className={classNames(
          'tw:rounded-md tw:p-xl tw:border',
          config.border,
          config.bg,
        )}
      >
        <Flex direction='column' gap='sm' className='tw:flex-1'>
          {/* Row 1: index + icon + name + status badge | close */}
          <Flex justify='between' alignItems='center'>
            <Flex alignItems='center' gap='sm' className='tw:flex-1'>
              <Text size='small' weight='semibold'>
                {index}.
              </Text>
              <i className={config.icon} />
              <Text size='small' weight='semibold'>
                {item.assetName || item.metadata?.file?.name}
              </Text>
              <Badge radius='squared' type={config.badgeType}>
                {badgeLabel}
              </Badge>
            </Flex>
            {!isAuditCompleted &&
              !isCertificateCompleted &&
              !isTracingProcessing && (
                <Tooltip title={tooltipLabel} placement='top'>
                  <Button
                    variant='ghost'
                    size='icon'
                    onClick={() =>
                      isProcessing || isCertifiedMaterialsCompleted
                        ? setConfirmOpen(true)
                        : handleDismiss()
                    }
                  >
                    <i className='fa-regular fa-xmark' />
                  </Button>
                </Tooltip>
              )}
          </Flex>

          {/* Progress row (PROCESSING only) */}
          {item.status === 'PROCESSING' && (
            <Flex alignItems='center' gap='sm'>
              <Box className='tw:flex-1'>
                <Progress className='tw:h-1' value={item.percentage} />
              </Box>
              <Text
                size='small'
                weight='semibold'
                className='tw:text-content-brand tw:whitespace-nowrap'
              >
                {getTranslation('itemCard.percentComplete', {
                  percentage: item.percentage,
                })}
              </Text>
            </Flex>
          )}

          {/* Row 2: asset type badge | action button */}
          <Flex justify='between' alignItems='center'>
            <Badge radius='squared' type={config.badgeType}>
              {getTranslation(`itemCard.assetType.${item.assetType}`, {
                defaultValue: item.assetType,
              })}
            </Badge>
            {item.status === 'COMPLETED' && (
              <Button variant='primary' size='sm' onClick={() => onCta(item)}>
                {getTranslation('itemCard.review')}
              </Button>
            )}
            {item.status === 'FAILED' && (
              <Button
                variant='fill-danger'
                size='sm'
                onClick={() => onCta(item)}
              >
                {getTranslation('itemCard.viewError')}
              </Button>
            )}
          </Flex>
        </Flex>
      </Flex>

      <CancelAnalysisDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        assetName={item.assetName}
        onConfirm={handleDismiss}
        variant={isCertifiedMaterialsCompleted ? 'dismissReview' : 'cancel'}
      />
    </>
  );
}
